import "pages/files/ClaimNumberInput.scss";
import "./AckEmailRecipientsForm.scss";

import { nanoid } from "@reduxjs/toolkit";
import { TextInput } from "components/inputs";
import { useEffect, useRef, useState } from "react";

const ActionButtons = ({
  onAdd,
  onDelete,
  canAdd = true,
  canDelete = false,
}: {
  onAdd: () => void;
  onDelete: () => void;
  canAdd?: boolean;
  canDelete?: boolean;
}) => (
  <figure className="buttons-container-add-delete">
    <div className="flex jc-end">
      {canAdd && (
        <button
          type="button"
          onClick={onAdd}
          className="btn add"
          aria-label="Add Field"
        >
          <span className="material-icons">add</span>
        </button>
      )}
      {canDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="btn delete"
          aria-label="Delete Field"
        >
          <span className="material-icons">delete</span>
        </button>
      )}
    </div>
  </figure>
);

// Extra addresses to CC on the referral acknowledgement email, in addition
// to the referring party. Local state until final submit, same pattern as
// DateOfInjuryForm's claim number rows -- not tied to a live backend record
// until the intake form itself is submitted.
const AckEmailRecipientsForm = ({
  initialEmails,
  onChange,
}: {
  initialEmails?: string[];
  onChange: (emails: string[]) => void;
}) => {
  // Each row carries a stable id (assigned once, on creation) so React keys
  // don't shift when a row in the middle of the list is deleted -- keying
  // on array index there would let React reconcile the wrong input's state.
  const [rows, setRows] = useState<{ id: string; value: string }[]>(() =>
    (initialEmails?.length ? initialEmails : [""]).map((value) => ({
      id: nanoid(),
      value,
    })),
  );

  // The parent passes onChange as a new inline function every render, so
  // keeping it as a useEffect dependency directly would refire on every
  // parent render, not just when rows actually change. Route through a ref
  // instead.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Pushed synchronously (no debounce) -- this is local string[] state, not
  // an expensive computation, and a debounce here previously let Skip &
  // Submit read stale react-hook-form state if confirmed within the
  // debounce window of the last keystroke.
  useEffect(() => {
    onChangeRef.current(
      rows.map((row) => row.value).filter((email) => email.trim() !== ""),
    );
  }, [rows]);

  const addEmail = () => {
    setRows((prev) => [...prev, { id: nanoid(), value: "" }]);
  };

  const updateEmail = (index: number, value: string) => {
    setRows((prev) => {
      const newRows = [...prev];
      newRows[index] = { ...newRows[index], value };
      return newRows;
    });
  };

  const deleteEmail = (index: number) => {
    setRows((prev) => {
      const newRows = prev.filter((_, i) => i !== index);
      return newRows.length > 0 ? newRows : [{ id: nanoid(), value: "" }];
    });
  };

  return (
    <div className="ack-email-recipients-form">
      <p className="ack-email-recipients-form__header">
        Acknowledgement Emails
      </p>
      <div className="multi-text-input-container">
        {rows.map((row, index) => (
          <div className="multi-row-adjust" key={row.id}>
            <label>{`Recipient ${index + 1}`}</label>
            <div className="input-container">
              <TextInput
                name={`ack_email_recipient_${index}`}
                value={row.value}
                type="email"
                onChange={(e) => updateEmail(index, e.target.value)}
              />
              <ActionButtons
                onAdd={addEmail}
                onDelete={() => deleteEmail(index)}
                canAdd={index === rows.length - 1}
                canDelete={rows.length > 1 || rows[0].value !== ""}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AckEmailRecipientsForm;
